import AddCircleIcon from "@material-ui/icons/AddCircle";
import React from "react";
import RemoveCircleIcon from "@material-ui/icons/RemoveCircle";

import { ApiErrors } from "common/api-errors/errors";
import { CommonErrorDialog } from "common/components/CommonErrorDialog";
import { errorMessages } from "common/utils/validation";
import { Formik, Form } from "formik";
import { Payment } from "common/store/payment/types";
import { PaymentInfoField } from "common/components/PaymentInfoField";
import { REGISTER_USER_DETAILS_ACTION } from "common/store/user/actions";
import { StoreState, ID } from "common/store/types";
import { useCustomQuery } from "common/store/hooks/useCustomQuery";
import { User } from "common/store/user/types";
import { UserInfoField, CommonToast, CommonButton } from "common/components";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  AddPayment,
  RemovePayment,
  SelectDefaultPayment,
} from "common/graphql/queryDescriptors";

import {
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Card,
  Typography,
  CircularProgress,
} from "@material-ui/core";

import "./RegisterUserAccountsForm.scss";

export const RegisterUserAccountsForm = () => {
  //State
  const [error, setError] = React.useState("");
  const [removedAccount, setRemovedAccount] = React.useState<Payment>(
    new Payment(null)
  );
  const [defaultAccountID, setDefaultAccountID] = React.useState<ID>("");
  const [toastOpen, setToastOpen] = React.useState(false);

  //Hooks
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const storageUserDetails = useSelector<StoreState, User>(
    (state) => state.currentUser
  );

  const { execute: addPayment, loading: addPaymentLoading } = useCustomQuery(
    AddPayment({
      onSuccess: () => {
        setToastOpen(true);
      },
    })
  );

  const {
    execute: removePayment,
    loading: removePaymentLoading,
  } = useCustomQuery(
    RemovePayment({
      onError: (error: ApiErrors) => {
        if (error === ApiErrors.USER_PAYMENT_DEFAULT) {
          setError(t("Default payment cannot be removed"));
        } else if (error === ApiErrors.USER_PAYMENT_NOT_FOUND) {
          setError(t("Cannot remove unknown payment"));
        }
      },
      onSuccess: () => {
        setToastOpen(true);

        let userDetails = new User(storageUserDetails);
        if (userDetails.payment_accounts) {
          userDetails.payment_accounts = userDetails.payment_accounts.filter(
            (account: Payment) => account.id !== removedAccount.id
          );
        }

        dispatch({
          type: REGISTER_USER_DETAILS_ACTION,
          payload: userDetails,
        });
      },
    })
  );

  const {
    execute: selectDefaultPayment,
    loading: selectDefaultPaymentLoading,
  } = useCustomQuery(
    SelectDefaultPayment({
      onError: (error: ApiErrors) => {
        if (error === ApiErrors.USER_PAYMENT_NOT_FOUND) {
          setError(t("Cannot set unknown payment as default"));
        } else if (error === ApiErrors.USER_PAYMENTS) {
          setError(t("There are no payments added"));
        }
      },
      onSuccess: () => {
        setToastOpen(true);

        let userDetails = new User(storageUserDetails);
        userDetails.default_payment = defaultAccountID;

        dispatch({
          type: REGISTER_USER_DETAILS_ACTION,
          payload: userDetails,
        });
      },
    })
  );

  //Validation
  const onValidateUser = (values: User) => {
    const errors: any = new User(values).validate(
      Object.keys(values),
      errorMessages
    );

    return errors;
  };

  const onValidatePayment = (values: Payment) => {
    const errors: any = new Payment(values).validate(
      Object.keys(values),
      errorMessages
    );

    return errors;
  };

  const onSubmit = async (values: User, { setSubmitting }: any) => {
    setSubmitting(true);

    setDefaultAccountID(values.default_payment);

    await selectDefaultPayment({
      variables: {
        id: values.default_payment,
      },
    });

    setSubmitting(false);
  };

  const removeAccount = async (account: Payment, { setSubmitting }: any) => {
    setSubmitting(true);

    setRemovedAccount(account);

    const data = new Payment(account).removeRequest();
    await removePayment({
      variables: data,
    });

    setSubmitting(false);
  };

  const addAccount = async (account: Payment, { setSubmitting }: any) => {
    setSubmitting(true);

    const data = new Payment(account).addRequest(
      storageUserDetails.payment_accounts.length === 0
    );

    await addPayment({
      variables: data,
    });

    setSubmitting(false);
  };

  //Options
  const excludedFields = ["BIC", "name"];
  const displayedPaymentFields = Payment.addFields.filter(
    (field) => !excludedFields.includes(field)
  );

  return (
    <>
      <Grid container direction="column" spacing={6}>
        <Grid item xs={12} className="section--nested">
          <Formik
            enableReinitialize
            validate={onValidateUser}
            onSubmit={onSubmit}
            initialValues={storageUserDetails}
          >
            {({
              submitForm,
              isSubmitting,
              setFieldValue,
              values,
              errors,
              touched,
            }) => {
              const props = {
                values,
                errors,
                touched,
                setFieldValue,
                disableImmutable: false,
              };

              return (
                <Form autoComplete="off">
                  <Grid container direction="column" spacing={6}>
                    <Grid item xs={12}>
                      <UserInfoField id="default_payment" {...props} />
                    </Grid>

                    <Grid container item direction="row">
                      {selectDefaultPaymentLoading || isSubmitting ? (
                        <CircularProgress />
                      ) : (
                        <CommonButton
                          onClick={submitForm}
                          isSubmitting={isSubmitting}
                          values={values}
                          errors={errors}
                          touched={touched}
                          text={t("Save")}
                        />
                      )}
                    </Grid>
                  </Grid>
                </Form>
              );
            }}
          </Formik>
        </Grid>

        {storageUserDetails.payment_accounts &&
          storageUserDetails.payment_accounts.length > 0 && (
            <Grid item xs={12} className="section--nested">
              <Typography className="section-title">{t("Accounts")}</Typography>

              <Card className={"card--nested"}>
                <Grid item xs={12}>
                  <List>
                    {storageUserDetails.payment_accounts.map(
                      (account: Payment, i: number) => (
                        <Formik
                          key={i}
                          enableReinitialize
                          validate={() => {}}
                          onSubmit={removeAccount}
                          initialValues={account}
                        >
                          {({
                            submitForm,
                            isSubmitting,
                            values,
                            errors,
                            touched,
                          }) => (
                            <Form>
                              <ListItem>
                                <ListItemText
                                  className="account-name"
                                  primary={account.IBAN}
                                />

                                <ListItemIcon>
                                  {removePaymentLoading || isSubmitting ? (
                                    <CircularProgress />
                                  ) : (
                                    <CommonButton
                                      disabled={
                                        account.id ===
                                        storageUserDetails.default_payment
                                      }
                                      onClick={submitForm}
                                      isSubmitting={isSubmitting}
                                      values={values}
                                      errors={errors}
                                      touched={touched}
                                      text={<RemoveCircleIcon />}
                                    />
                                  )}
                                </ListItemIcon>
                              </ListItem>
                            </Form>
                          )}
                        </Formik>
                      )
                    )}
                  </List>
                </Grid>
              </Card>
            </Grid>
          )}

        <Grid item xs={12} className="section--nested">
          <Typography className="section-title">{t("Add account")}</Typography>

          <Card className={"card--nested"}>
            <Formik
              enableReinitialize
              validate={onValidatePayment}
              onSubmit={addAccount}
              initialValues={new Payment(null)}
            >
              {({
                submitForm,
                isSubmitting,
                setFieldValue,
                values,
                errors,
                touched,
              }) => {
                const props = {
                  values,
                  errors,
                  touched,
                  setFieldValue,
                  disableImmutable: false,
                };

                return (
                  <Form autoComplete="off">
                    <Grid container direction="column" spacing={3}>
                      <Grid item></Grid>

                      <Grid item container className="fields-container">
                        {displayedPaymentFields.map((field, i) => (
                          <Grid
                            item
                            xs={12}
                            md={6}
                            className="field-wrapper"
                            key={i}
                          >
                            <PaymentInfoField id={field} {...props} />
                          </Grid>
                        ))}
                      </Grid>

                      <Grid item>
                        {addPaymentLoading || isSubmitting ? (
                          <CircularProgress />
                        ) : (
                          <CommonButton
                            onClick={submitForm}
                            isSubmitting={isSubmitting}
                            values={values}
                            errors={errors}
                            touched={touched}
                            text={<AddCircleIcon />}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Form>
                );
              }}
            </Formik>
          </Card>
        </Grid>
      </Grid>

      <CommonErrorDialog error={error} setError={setError} />

      <CommonToast
        open={toastOpen}
        onClose={() => setToastOpen(false)}
        text={t("Changes saved!")}
        severity={"success"}
      />
    </>
  );
};
